import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, Check, X, ChevronDown, ChevronUp, Star, ArrowUp, ArrowDown } from 'lucide-react'
import { useCategoryStore } from '../../store/categoryStore'

export default function CategoriesManager() {
  const { fetchCategories, addCategory, updateCategory, deleteCategory, reorderCategory, toggleShortcut, getTree, isLoading } = useCategoryStore()
  const tree = getTree()

  const [newRootName, setNewRootName] = useState('')
  const [newSubName, setNewSubName] = useState<Record<string, string>>({})
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [isReordering, setIsReordering] = useState(false)

  useEffect(() => { fetchCategories() }, [fetchCategories])

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const startEdit = (id: string, name: string) => {
    setEditingId(id)
    setEditingName(name)
  }

  const confirmEdit = async (id: string) => {
    if (!editingName.trim()) return
    await updateCategory(id, editingName.trim())
    setEditingId(null)
  }

  const handleAddRoot = async () => {
    if (!newRootName.trim()) return
    await addCategory(newRootName.trim(), null)
    setNewRootName('')
  }

  const handleAddSub = async (parentId: string) => {
    const name = newSubName[parentId]?.trim()
    if (!name) return
    await addCategory(name, parentId)
    setNewSubName(prev => ({ ...prev, [parentId]: '' }))
  }

  // const moveItem = async (items: { id: string; order: number }[], index: number, dir: -1 | 1) => {
  //   const newIndex = index + dir
  //   if (newIndex < 0 || newIndex >= items.length) return
  //   const reordered = [...items]
  //   const [moved] = reordered.splice(index, 1)
  //   reordered.splice(newIndex, 0, moved)
  //   await Promise.all(reordered.map((item, i) => reorderCategory(item.id, i)))
  //   await fetchCategories()
  // }
  const moveItem = async (items: { id: string; order: number }[], index: number, dir: -1 | 1) => {
    const newIndex = index + dir
    if (newIndex < 0 || newIndex >= items.length) return
    setIsReordering(true)
    const reordered = [...items]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(newIndex, 0, moved)
    await Promise.all(reordered.map((item, i) => reorderCategory(item.id, i)))
    await fetchCategories()
    setIsReordering(false)
  }

  if (isLoading) return (
    <div className="flex flex-col gap-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-cream rounded-[12px] h-[82px] overflow-hidden relative">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </div>
      ))}
    </div>
  )

  return (
    <div className="flex flex-col gap-6.5 lg:gap-4">
      {tree.map(root => (
        <div key={root.id} className="bg-cream rounded-[12px] overflow-hidden">
          <div className="flex items-center gap-3.5 lg:gap-2 px-4 py-3">
            <button
              onClick={() => toggleExpand(root.id)}
              className="text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
            >
              {expandedIds.has(root.id)
                ? <ChevronUp className="w-4 h-6 lg:w-4 lg:h-4" />
                : <ChevronDown className="w-4 h-6 lg:w-4 lg:h-4" />}
            </button>

            {editingId === root.id ? (
              <div className="flex items-center gap-3 flex-1">
                <input
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') confirmEdit(root.id); if (e.key === 'Escape') setEditingId(null) }}
                  className="flex-1 px-3 py-1.5 rounded-[8px] border border-cream-dark text-sm outline-none focus:border-green-primary"
                  autoFocus
                />
                <button onClick={() => confirmEdit(root.id)} className="text-green-primary hover:opacity-70"><Check className="w-4 h-4" /></button>
                <button onClick={() => setEditingId(null)} className="text-text-secondary hover:opacity-70"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <span className="flex-1 font-semibold text-text-primary text-lg lg:text-sm">{root.name}</span>
            )}

            <div className="flex items-center gap-3 lg:gap-1">
              <button onClick={() => moveItem(tree as any, tree.indexOf(root), -1)} disabled={isReordering || tree.indexOf(root) === 0}
                className="py-1.5 px-1 rounded-[6px] hover:bg-white text-text-secondary disabled:opacity-30 cursor-pointer">
                <ArrowUp className="w-4 h-4" />
              </button>
              <button onClick={() => moveItem(tree as any, tree.indexOf(root), 1)} disabled={isReordering || tree.indexOf(root) === tree.length - 1}
                className="py-1.5 px-1 rounded-[6px] hover:bg-white text-text-secondary disabled:opacity-30 cursor-pointer">
                <ArrowDown className="w-4 h-4" />
              </button>
              <div className="w-[2px] h-4 bg-[#6B6560] opacity-25 mx-3 lg:mx-5" />
              <button onClick={() => startEdit(root.id, root.name)} className="p-1.5 rounded-[6px] hover:bg-white text-text-secondary hover:text-text-primary transition-colors cursor-pointer">
                <Pencil className="w-5 h-5 lg:w-3.5 lg:h-3.5" />
              </button>
              <button onClick={() => {
                if (confirm(`אתה עומד למחוק את הקטגוריה ${root.name}. האם אתה בטוח?`)) {
                  deleteCategory(root.id)
                }
                }} className="p-1.5 rounded-[6px] hover:bg-white text-text-secondary hover:text-red-500 transition-colors cursor-pointer">
                <Trash2 className="w-5 h-5 lg:w-3.5 lg:h-3.5" />
              </button>
            </div>
          </div>

          {expandedIds.has(root.id) && (
            <div className="border-t border-cream-dark">
              {root.children.map(child => (
                <div key={child.id} className="flex items-center gap-2 px-4 py-2.5 pr-10 bg-white border-b border-cream-dark last:border-b-0">
                  {editingId === child.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') confirmEdit(child.id); if (e.key === 'Escape') setEditingId(null) }}
                        className="flex-1 px-3 py-1.5 rounded-[8px] border border-cream-dark text-sm outline-none focus:border-green-primary"
                        autoFocus
                      />
                      <button onClick={() => moveItem(root.children, root.children.indexOf(child), -1)} disabled={root.children.indexOf(child) === 0}
                        className="py-1.5 px-1 rounded-[6px] hover:bg-white text-text-secondary disabled:opacity-30 cursor-pointer">
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveItem(root.children, root.children.indexOf(child), 1)} disabled={root.children.indexOf(child) === root.children.length - 1}
                        className="py-1.5 px-1 rounded-[6px] hover:bg-white text-text-secondary disabled:opacity-30 cursor-pointer">
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => confirmEdit(child.id)} className="text-green-primary hover:opacity-70"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingId(null)} className="text-text-secondary hover:opacity-70"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <span className="flex-1 text-md lg:text-sm text-text-primary">{child.name}</span>
                  )}
                  <div className="flex items-center gap-2 lg:gap-1">
                    <button onClick={() => moveItem(root.children, root.children.indexOf(child), -1)} disabled={root.children.indexOf(child) === 0}
                      className="py-1.5 px-1 rounded-[6px] hover:bg-white text-text-secondary disabled:opacity-30 cursor-pointer">
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveItem(root.children, root.children.indexOf(child), 1)} disabled={root.children.indexOf(child) === root.children.length - 1}
                      className="py-1.5 px-1 rounded-[6px] hover:bg-white text-text-secondary disabled:opacity-30 cursor-pointer">
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-cream-dark mx-1.5" />
                    <button
                      onClick={() => toggleShortcut(child.id, child.is_shortcut)}
                      title={child?.is_shortcut ? 'הסר מקיצורים' : 'הוסף לקיצורים'}
                      className={`p-1.5 rounded-[6px] transition-colors cursor-pointer
                        ${child.is_shortcut
                          ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100'
                          : 'text-text-secondary hover:bg-cream hover:text-yellow-500'
                        }`}
                    >
                      <Star className={`w-4 h-4 lg:w-3.5 lg:h-3.5 ${child.is_shortcut ? 'fill-yellow-500' : ''}`} />
                    </button>
                    <button onClick={() => startEdit(child.id, child.name)} className="p-1.5 rounded-[6px] hover:bg-cream text-text-secondary hover:text-text-primary transition-colors cursor-pointer">
                      <Pencil className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                    </button>
                    <button onClick={() => deleteCategory(child.id)} className="p-1.5 rounded-[6px] hover:bg-cream text-text-secondary hover:text-red-500 transition-colors cursor-pointer">
                      <Trash2 className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex items-center gap-2 px-4 py-2.5 pr-10 bg-white">
                <input
                  value={newSubName[root.id] ?? ''}
                  onChange={e => setNewSubName(prev => ({ ...prev, [root.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddSub(root.id) }}
                  placeholder="הוסף סוג..."
                  className="flex-1 px-3 py-1.5 rounded-[8px] border border-cream-dark text-sm outline-none focus:border-green-primary bg-cream"
                />
                <button
                  onClick={() => handleAddSub(root.id)}
                  className="p-1.5 rounded-[6px] bg-green-primary text-white hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="flex items-center gap-2">
        <input
          value={newRootName}
          onChange={e => setNewRootName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAddRoot() }}
          placeholder="הוסף קטגוריה ראשית..."
          className="flex-1 px-4 py-2.5 rounded-[10px] border border-cream-dark text-sm outline-none focus:border-green-primary bg-white"
        />
        <button
          onClick={handleAddRoot}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] bg-green-primary text-white text-sm font-medium hover:opacity-80 transition-opacity cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          הוסף
        </button>
      </div>
    </div>
  )
}